import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Appointment,
  AppointmentPaymentSummary,
  AppointmentPaymentStatus,
  Doctor
} from './components/calendar/types';
import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';
import { useThemedStyles } from './theme/useThemedStyles';
import { parseTreatedTeeth, ToothSelector } from './components/calendar/ToothSelector';
import { ModalBackdrop } from './components/common/ModalBackdrop';
import { BolivianDateTimeInput } from './components/common/BolivianDateInput';
import { formatDateTime } from './utils/dateFormat';
import './AppointmentList.css';

interface AppointmentFormState {
  startTime: string;
  endTime: string;
  reason: string;
  notes: string;
  color: string;
  chargeAmount: string;
  includeTreatment: boolean;
  treatmentName: string;
  treatmentCost: string;
  treatedTeeth: string[];
}

export interface AppointmentFinancials {
  charge: number | null;
  paid: number;
  outstanding: number | null;
  status: AppointmentPaymentStatus;
}

const emptyForm = (): AppointmentFormState => ({
  startTime: '',
  endTime: '',
  reason: '',
  notes: '',
  color: '#667eea',
  chargeAmount: '',
  includeTreatment: false,
  treatmentName: '',
  treatmentCost: '',
  treatedTeeth: []
});

const toLocalInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const paymentNet = (payments: AppointmentPaymentSummary[] = []) =>
  payments.reduce(
    (sum, payment) => sum + (payment.kind === 'refund' ? -payment.amount : payment.amount),
    0
  );

export const getAppointmentReason = (appointment: Appointment): string =>
  appointment.customReason?.trim()
  || appointment.treatmentTemplate?.name?.trim()
  || appointment.treatments?.map(item => item.name).filter(Boolean).join(', ')
  || appointment.treatment?.name?.trim()
  || '';

export const getAppointmentFinancials = (appointment: Appointment): AppointmentFinancials => {
  const paid = Math.max(0, paymentNet(appointment.payments));
  const charge = typeof appointment.chargeAmount === 'number' ? appointment.chargeAmount : null;
  const outstanding = charge === null ? null : Math.max(0, charge - paid);
  let status = appointment.paymentStatus || 'unpaid';
  if (charge !== null && status !== 'refunded') {
    status = charge <= 0.005 || outstanding! <= 0.005
      ? 'paid'
      : paid > 0.005
        ? 'partially_paid'
        : 'unpaid';
  }
  return { charge, paid, outstanding, status };
};

export const partitionAppointments = (appointments: Appointment[], now = new Date()) => {
  const timestamp = now.getTime();
  const future = appointments
    .filter(item => item.startTime && new Date(item.startTime).getTime() >= timestamp)
    .sort((a, b) => new Date(a.startTime!).getTime() - new Date(b.startTime!).getTime());
  const past = appointments
    .filter(item => !item.startTime || new Date(item.startTime).getTime() < timestamp)
    .sort((a, b) => {
      if (!a.startTime) return 1;
      if (!b.startTime) return -1;
      return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
    });
  return { future, past };
};

export default function AppointmentList({ token, patientId }: { token: string; patientId: number }) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [form, setForm] = useState<AppointmentFormState>(emptyForm);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAppointments = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_APPOINTMENTS, { patientId }), {
        headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error(t('appointments.loadFailed'));
      const data = await response.json();
      setAppointments(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('appointments.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) })
      .then(response => response.ok ? response.json() : [])
      .then(data => setDoctors(Array.isArray(data) ? data : []))
      .catch(() => setDoctors([]));
  // Data source changes only with patient/token.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, patientId]);

  const grouped = useMemo(() => partitionAppointments(appointments), [appointments]);
  const doctorById = useMemo(() => new Map(doctors.map(doctor => [doctor.id, doctor])), [doctors]);

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingAppointment(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditingAppointment(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  };

  const openEdit = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    setForm({
      startTime: toLocalInputValue(appointment.startTime),
      endTime: toLocalInputValue(appointment.endTime),
      reason: getAppointmentReason(appointment),
      notes: appointment.notes || '',
      color: appointment.color || '#667eea',
      chargeAmount: appointment.chargeAmount == null ? '' : String(appointment.chargeAmount),
      includeTreatment: false,
      treatmentName: '',
      treatmentCost: '',
      treatedTeeth: parseTreatedTeeth(appointment.treatedTeeth)
    });
    setError('');
    setModalOpen(true);
  };

  const updateForm = <K extends keyof AppointmentFormState>(key: K, value: AppointmentFormState[K]) =>
    setForm(current => ({ ...current, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.startTime || !form.endTime || !form.reason.trim()) return;
    const startTime = new Date(form.startTime);
    const endTime = new Date(form.endTime);
    if (endTime <= startTime) {
      setError(t('appointments.endAfterStart'));
      return;
    }

    const parsedCharge = form.chargeAmount === '' ? null : Number(form.chargeAmount);
    if (parsedCharge !== null && (!Number.isFinite(parsedCharge) || parsedCharge < 0)) {
      setError(t('appointments.invalidCharge'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      const commonPayload = {
        patientId,
        doctorId: editingAppointment?.doctorId ?? null,
        secondDoctorId: editingAppointment?.secondDoctorId ?? null,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        duration: Math.max(1, Math.round((endTime.getTime() - startTime.getTime()) / 60_000)),
        notes: form.notes,
        color: form.color,
        marginColor: editingAppointment?.marginColor ?? null,
        room: editingAppointment?.room ?? null,
        status: editingAppointment?.status || 'scheduled',
        isNoShow: editingAppointment?.isNoShow || false,
        isAllDay: false,
        treatmentId: editingAppointment?.treatmentId ?? null,
        treatmentTemplateId: editingAppointment?.treatmentTemplateId ?? null,
        customReason: editingAppointment?.treatmentTemplateId ? null : form.reason.trim(),
        chargeAmount: parsedCharge,
        treatedTeeth: form.treatedTeeth.join(',')
      };

      const endpoint = editingAppointment
        ? `${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}`
        : API_CONFIG.ENDPOINTS.APPOINTMENTS;
      const payload = editingAppointment || !form.includeTreatment
        ? commonPayload
        : {
            ...commonPayload,
            newTreatment: {
              name: form.treatmentName.trim(),
              cost: Number(form.treatmentCost)
            }
          };

      const response = await fetch(getApiUrl(endpoint), {
        method: editingAppointment ? 'PUT' : 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(editingAppointment ? t('appointments.updateFailed') : t('appointments.createFailed'));

      if (editingAppointment && parsedCharge !== editingAppointment.chargeAmount) {
        const financeResponse = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${editingAppointment.id}/finance`), {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify({ chargeAmount: parsedCharge })
        });
        if (!financeResponse.ok) throw new Error(t('appointments.financeUpdateFailed'));
      }

      setModalOpen(false);
      setEditingAppointment(null);
      setForm(emptyForm());
      await fetchAppointments();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('appointments.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('appointments.deleteConfirm'))) return;
    setError('');
    try {
      const response = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.APPOINTMENTS}/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error(t('appointments.deleteFailed'));
      await fetchAppointments();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('appointments.deleteFailed'));
    }
  };

  const formatMoney = (value: number | null) => value === null ? '—' : `Bs ${value.toFixed(2)}`;
  const appointmentDateTime = (value: string | null) => formatDateTime(value, t('appointments.unscheduled'));

  const renderGroup = (title: string, items: Appointment[]) => (
    <section className="patient-appointment-group">
      <div className="patient-appointment-group__heading">
        <h3>{title}</h3>
        <span>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="patient-appointment-empty" style={{ ...theme.panel, color: theme.muted }}>
          {t('appointments.noneInGroup')}
        </div>
      ) : (
        <div className="patient-appointment-list">
          {items.map(appointment => {
            const financials = getAppointmentFinancials(appointment);
            const doctor = appointment.doctor || (appointment.doctorId ? doctorById.get(appointment.doctorId) : undefined);
            const doctorName = doctor ? `${doctor.firstName} ${doctor.lastName}`.trim() : t('appointments.noDoctor');
            return (
              <article key={appointment.id} className="patient-appointment-card" style={theme.card}>
                <div className="patient-appointment-card__accent" style={{ background: appointment.color || theme.primary }} />
                <div className="patient-appointment-card__main">
                  <div className="patient-appointment-card__title">
                    <div>
                      <strong>{getAppointmentReason(appointment) || t('appointments.noReason')}</strong>
                      <div style={{ color: theme.muted }}>{appointmentDateTime(appointment.startTime)}</div>
                    </div>
                    <div className="patient-appointment-badges">
                      <span style={{ background: theme.subtle, color: theme.text }}>{t(`appointments.statuses.${appointment.status || 'scheduled'}`)}</span>
                      <span className={`payment-status payment-status--${financials.status}`}>
                        {t(`appointments.paymentStatuses.${financials.status}`)}
                      </span>
                    </div>
                  </div>
                  <div className="patient-appointment-card__meta" style={{ color: theme.muted }}>
                    <span>{t('appointments.doctor')}: {doctorName}</span>
                    {appointment.notes && <span>{appointment.notes}</span>}
                  </div>
                  <div className="patient-appointment-finance">
                    <div><small>{t('appointments.charge')}</small><strong>{formatMoney(financials.charge)}</strong></div>
                    <div><small>{t('appointments.paid')}</small><strong>{formatMoney(financials.paid)}</strong></div>
                    <div className={financials.outstanding !== null && financials.outstanding > 0.005 ? 'patient-appointment-finance__outstanding' : undefined}>
                      <small>{t('appointments.outstanding')}</small><strong>{formatMoney(financials.outstanding)}</strong>
                    </div>
                  </div>
                </div>
                <div className="patient-appointment-card__actions">
                  <button type="button" style={theme.secondaryButton} onClick={() => openEdit(appointment)}>{t('common.edit')}</button>
                  <button type="button" className="danger-button" onClick={() => handleDelete(appointment.id)}>{t('common.delete')}</button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );

  if (loading) return <div style={{ color: theme.text }}>{t('appointments.loading')}</div>;

  return (
    <div className="patient-appointments">
      <div className="patient-appointments__toolbar">
        <div>
          <h2>{t('appointments.title')}</h2>
          <p style={{ color: theme.muted }}>{t('appointments.summary', { count: appointments.length })}</p>
        </div>
        <button type="button" style={theme.button} onClick={openCreate}>+ {t('appointments.new')}</button>
      </div>
      {error && <div className="patient-appointment-error">{error}</div>}
      {renderGroup(t('appointments.future'), grouped.future)}
      {renderGroup(t('appointments.past'), grouped.past)}

      {modalOpen && (
        <ModalBackdrop className="patient-appointment-modal" role="presentation" onDismiss={closeModal}>
          <div className="patient-appointment-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="appointment-form-title" style={theme.card}>
            <div className="patient-appointment-modal__header">
              <h3 id="appointment-form-title">{editingAppointment ? t('appointments.edit') : t('appointments.new')}</h3>
              <button type="button" aria-label={t('common.close', 'Close')} style={theme.secondaryButton} onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="patient-appointment-form-grid">
                <label>
                  <span>{t('appointments.start')}</span>
                  <BolivianDateTimeInput required value={form.startTime} onChange={value => updateForm('startTime', value)} style={theme.input} />
                </label>
                <label>
                  <span>{t('appointments.end')}</span>
                  <BolivianDateTimeInput required value={form.endTime} onChange={value => updateForm('endTime', value)} style={theme.input} />
                </label>
                <label className="patient-appointment-form-grid__wide">
                  <span>{t('appointments.reason')}</span>
                  <input
                    required
                    disabled={Boolean(editingAppointment?.treatmentTemplateId)}
                    value={form.reason}
                    onChange={event => updateForm('reason', event.target.value)}
                    style={theme.input}
                  />
                </label>
                <label>
                  <span>{t('appointments.charge')}</span>
                  <input type="number" min="0" step="0.01" value={form.chargeAmount} onChange={event => updateForm('chargeAmount', event.target.value)} style={theme.input} />
                </label>
                <label>
                  <span>{t('appointments.color')}</span>
                  <input type="color" value={form.color} onChange={event => updateForm('color', event.target.value)} style={theme.input} />
                </label>
                <label className="patient-appointment-form-grid__wide">
                  <span>{t('appointments.notes')}</span>
                  <textarea value={form.notes} onChange={event => updateForm('notes', event.target.value)} style={theme.input} />
                </label>
              </div>
              <ToothSelector value={form.treatedTeeth} onChange={treatedTeeth => updateForm('treatedTeeth', treatedTeeth)} />
              {!editingAppointment && (
                <div className="patient-appointment-treatment" style={theme.panel}>
                  <label className="patient-appointment-checkbox">
                    <input type="checkbox" checked={form.includeTreatment} onChange={event => updateForm('includeTreatment', event.target.checked)} />
                    <span>{t('appointments.addTreatment')}</span>
                  </label>
                  {form.includeTreatment && (
                    <div className="patient-appointment-form-grid">
                      <label>
                        <span>{t('appointments.treatmentName')}</span>
                        <input required value={form.treatmentName} onChange={event => updateForm('treatmentName', event.target.value)} style={theme.input} />
                      </label>
                      <label>
                        <span>{t('appointments.treatmentCost')}</span>
                        <input required type="number" min="0" step="0.01" value={form.treatmentCost} onChange={event => updateForm('treatmentCost', event.target.value)} style={theme.input} />
                      </label>
                    </div>
                  )}
                </div>
              )}
              <div className="patient-appointment-modal__actions">
                <button type="button" style={theme.secondaryButton} onClick={closeModal}>{t('common.cancel')}</button>
                <button type="submit" style={theme.button} disabled={saving}>{saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}</button>
              </div>
            </form>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
